import React, { useState, useCallback, useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { isMobileDevice } from '../utils/deviceDetection';
import { setPendingRedirectTarget } from '../utils/postLoginRedirect';
import { apiRequest } from '../authConfig';
import { resetTokenRecovery } from '../services/getApiToken';

/**
 * Global Auth Error Modal - displayed when user's authentication expires or permission is denied
 * Handles both session expiration (401) and permission issues (403)
 */
export const AuthErrorModal = ({ show, onClose, error }) => {
  const { instance, inProgress } = useMsal();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [signInError, setSignInError] = useState('');

  const isPermissionError = error?.type === 'PERMISSION_DENIED' || error?.isPermissionError;
  const isConsentRequest = error?.isConsentRequest
    || error?.request?.scopes?.some((scope) => scope.toLowerCase() === 'user.readbasic.all');

  const errorContent = useMemo(() => {
    if (isPermissionError) {
      return {
        title: 'Access Denied',
        icon: '🔒',
        message: 'You do not have permission to access this resource.',
        details: error?.message || 'Your account does not have the required privileges for this operation.',
        showSignInButton: false,
        showContactAdmin: true
      };
    } else {
      return {
        title: isConsentRequest ? 'Additional Sign-In Required' : 'Authentication Required',
        icon: '⚠️',
        message: isConsentRequest
          ? 'Additional permission is required for this request.'
          : 'Your authentication session has expired.',
        details: isConsentRequest
          ? 'Continue to Microsoft to grant access, or close this prompt to continue without directory search.'
          : 'Please sign in again to continue.',
        showSignInButton: true,
        showContactAdmin: false
      };
    }
  }, [isConsentRequest, isPermissionError, error]);

  const handleSignIn = useCallback(async () => {
    setIsSigningIn(true);
    setSignInError('');
    try {
      const request = { ...(error?.request || apiRequest) };
      // Popups are unreliable/silently blocked on mobile browsers and in-app webviews.
      if (isMobileDevice()) {
        setPendingRedirectTarget(`${window.location.pathname}${window.location.search}${window.location.hash}`);
        await instance.acquireTokenRedirect(request);
        return;
      }
      const response = await instance.acquireTokenPopup(request);
      instance.setActiveAccount(response.account);
      resetTokenRecovery(instance, request);
      if (isConsentRequest) {
        onClose?.();
        return;
      }
      // Refresh the page after successful sign-in
      window.location.reload();
    } catch (err) {
      console.error('[Auth Modal] Sign-in failed:', err);
      setSignInError('Sign-in did not complete. Please try again.');
      setIsSigningIn(false);
    }
  }, [instance, error, isConsentRequest, onClose]);

  const handleClose = useCallback(() => {
    setIsSigningIn(false);
    if (isConsentRequest) resetTokenRecovery(instance, error?.request);
    onClose?.();
  }, [error, instance, isConsentRequest, onClose]);

  const canDismiss = isPermissionError || isConsentRequest;

  return (
    <Modal show={show} onHide={canDismiss ? handleClose : undefined} backdrop="static" keyboard={false} centered>
      <Modal.Header closeButton={canDismiss && !isSigningIn}>
        <Modal.Title>
          <span className="me-2">{errorContent.icon}</span>
          {errorContent.title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={`alert ${isPermissionError ? 'alert-danger' : 'alert-warning'} mb-3`}>
          <strong>{errorContent.message}</strong>
        </div>
        <p className="text-muted mb-2">
          {errorContent.details}
        </p>
        {signInError && <p role="alert" className="text-danger">{signInError}</p>}
        {error?.url && (
          <p className="text-muted small mb-0">
            <strong>Resource:</strong> {error.url}
          </p>
        )}
        {error?.message && !errorContent.details?.includes(error.message) && (
          <p className="text-muted small mt-2 mb-0">
            <strong>Error:</strong> {error.message}
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        {errorContent.showContactAdmin && (
          <span className="text-muted small">
            Please contact your administrator if you believe this is an error.
          </span>
        )}
        {errorContent.showSignInButton && (
          <Button 
            variant="primary" 
            onClick={handleSignIn}
            disabled={isSigningIn || (inProgress && inProgress !== 'none')}
          >
            {isSigningIn ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Signing In...
              </>
            ) : (
              isConsentRequest ? 'Continue' : 'Sign In Again'
            )}
          </Button>
        )}
        {canDismiss && (
          <Button 
            variant="secondary" 
            onClick={handleClose}
          >
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AuthErrorModal;
